import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { cn } from "@/lib/utils.ts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildCron,
  categoryToFrequency,
  type CronFrequency,
  DAY_LABELS,
  describeCron,
  type EveryInterval,
  everyIntervalOptions,
  frequencyToCategory,
  frequencyToInterval,
  isEveryInterval,
  isIntervalFrequency,
  isRepeatCategory,
  isValidCron,
  parseCron,
  repeatCategoryOptions,
  type RepeatCategory,
} from "./cronUtils.ts";
import { TimeOfDaySelect } from "./TimeOfDaySelect.tsx";

/**
 * The cron schedule editor: Repeat, the controls that repeat implies (interval,
 * time of day, weekdays, day of month, or a raw expression), and a plain-English
 * summary of what they add up to.
 *
 * One component for every surface that edits a schedule — the routine form, the
 * routine artifact's `cronSchedule` block, and the General tab of a routine okthx
 * ships. They differ only in where the emitted cron goes: form state, an artifact
 * patch, or a per-user schedule override.
 *
 * Fully controlled. Every edit emits a cron rebuilt from *all* the controls, so
 * the caller must feed the new value back as `value` — see the re-seed effect.
 */
export const CronScheduleEditor = ({
  value,
  onChange,
  disabled = false,
  timezone,
}: {
  /** The current 5-field cron expression. */
  value: string;
  /** Fires with the rebuilt expression on every edit. */
  onChange: (schedule: string) => void;
  /** Render the controls inert — the schedule is shown but not the user's to move. */
  disabled?: boolean;
  /** Named in the summary line, so the stated time is not read in the wrong zone. */
  timezone?: string;
}) => {
  const parsed = useMemo(() => parseCron(value), [value]);

  const [frequency, setFrequency] = useState<CronFrequency>(parsed?.frequency ?? "custom");
  const [hours, setHours] = useState<number[]>(parsed?.hours ?? [9]);
  const [minute, setMinute] = useState(parsed?.minute ?? 0);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(parsed?.daysOfWeek ?? [1]);
  const [dayOfMonth, setDayOfMonth] = useState(parsed?.dayOfMonth ?? 1);
  const [customDraft, setCustomDraft] = useState(value);
  const [customError, setCustomError] = useState<string | null>(null);

  // The structured controls seed from `value` once (the useState initializers
  // above), but the canonical schedule can change under us — the canvas routine
  // card overlays the live routine's schedule once it loads, and a refetch can
  // bring a value edited elsewhere. Re-seed when it does. Without this the
  // controls hold the stale seed, and since every edit emits a cron rebuilt from
  // ALL of them, changing one field (say the hour) silently reverts the others
  // (the repeat/days) to the stale values. Our own emits set `value` to exactly
  // what we just built, so re-seeding to it is a no-op; the ref guard keeps that
  // from looping.
  const syncedValue = useRef(value);
  useEffect(() => {
    if (value === syncedValue.current) return;
    syncedValue.current = value;
    const next = parseCron(value);
    setFrequency(next?.frequency ?? "custom");
    setHours(next?.hours ?? [9]);
    setMinute(next?.minute ?? 0);
    setDaysOfWeek(next?.daysOfWeek ?? [1]);
    setDayOfMonth(next?.dayOfMonth ?? 1);
    setCustomDraft(value);
    setCustomError(null);
  }, [value]);

  const handleCustomBlur = useCallback(() => {
    const trimmed = customDraft.trim();
    if (!isValidCron(trimmed)) {
      setCustomError("Invalid cron: expected 5 fields (minute hour day month weekday)");
      return;
    }
    setCustomError(null);
    if (trimmed !== value) onChange(trimmed);
  }, [customDraft, value, onChange]);

  const category = frequencyToCategory(frequency);
  const interval = frequencyToInterval(frequency);

  const emitChange = useCallback(
    (f: Exclude<CronFrequency, "custom">, h: number[], m: number, dow: number[], dom: number) => {
      onChange(buildCron(f, h, m, dow, dom));
    },
    [onChange],
  );

  const handleCategoryChange = useCallback(
    (cat: RepeatCategory) => {
      if (cat === "custom") {
        setFrequency("custom");
        setCustomDraft(value);
        setCustomError(null);
        return;
      }
      const newFreq = categoryToFrequency(cat, interval);
      setFrequency(newFreq);
      const dow = newFreq === "weekly" ? (daysOfWeek.length > 0 ? daysOfWeek : [1]) : daysOfWeek;
      if (newFreq === "weekly") setDaysOfWeek(dow);
      emitChange(newFreq, hours, minute, dow, dayOfMonth);
    },
    [emitChange, hours, minute, daysOfWeek, dayOfMonth, interval, value],
  );

  const handleIntervalChange = useCallback(
    (iv: EveryInterval) => {
      setFrequency(iv);
      emitChange(iv, hours, minute, daysOfWeek, dayOfMonth);
    },
    [emitChange, hours, minute, daysOfWeek, dayOfMonth],
  );

  const handleTimeChange = useCallback(
    (h: number[], m: number) => {
      if (frequency === "custom") return;
      setHours(h);
      setMinute(m);
      emitChange(frequency, h, m, daysOfWeek, dayOfMonth);
    },
    [emitChange, frequency, daysOfWeek, dayOfMonth],
  );

  const toggleDay = useCallback(
    (day: number) => {
      if (frequency === "custom") return;
      const next = daysOfWeek.includes(day)
        ? daysOfWeek.filter((d) => d !== day)
        : [...daysOfWeek, day];
      if (next.length === 0) return;
      setDaysOfWeek(next);
      emitChange(frequency, hours, minute, next, dayOfMonth);
    },
    [emitChange, frequency, hours, minute, daysOfWeek, dayOfMonth],
  );

  const handleDayOfMonthChange = useCallback(
    (d: string) => {
      if (frequency === "custom") return;
      const n = Math.max(1, Math.min(31, Number(d) || 1));
      setDayOfMonth(n);
      emitChange(frequency, hours, minute, daysOfWeek, n);
    },
    [emitChange, frequency, hours, minute, daysOfWeek],
  );

  // An interval schedule ("every 15 minutes") consumes the time fields, so it has
  // no time of day to state; a raw expression states its own.
  const showTimeControls = !isIntervalFrequency(frequency) && frequency !== "custom";

  return (
    <div className="space-y-2 mt-1.5">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground w-16 shrink-0">Repeat</span>
        <Select
          value={category}
          disabled={disabled}
          onValueChange={(v) => {
            if (isRepeatCategory(v)) handleCategoryChange(v);
          }}
        >
          <SelectTrigger className="h-7 text-xs w-28 rounded-md">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {repeatCategoryOptions.map(({ value: option, label }) => (
              <SelectItem key={option} value={option}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {category === "every" && (
          <Select
            value={interval}
            disabled={disabled}
            onValueChange={(v) => {
              if (isEveryInterval(v)) handleIntervalChange(v);
            }}
          >
            <SelectTrigger className="h-7 text-xs w-32 rounded-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {everyIntervalOptions.map(({ value: option, label }) => (
                <SelectItem key={option} value={option}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {showTimeControls && (
        <TimeOfDaySelect
          hours={hours}
          minute={minute}
          disabled={disabled}
          onChange={handleTimeChange}
        />
      )}

      {frequency === "weekly" && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-16 shrink-0">On</span>
          <div className="flex gap-1">
            {DAY_LABELS.map((label, i) => (
              <Button
                key={label}
                variant="blank"
                size="fit"
                disabled={disabled}
                onClick={() => toggleDay(i)}
                data-statsig-name={`cron-schedule-day-${label.toLowerCase()}`}
                className={cn(
                  "h-7 w-9 rounded-md text-xs font-medium",
                  daysOfWeek.includes(i)
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80",
                )}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {frequency === "monthly" && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-16 shrink-0">On day</span>
          <Input
            type="number"
            min={1}
            max={31}
            value={dayOfMonth}
            readOnly={disabled}
            onChange={(e) => handleDayOfMonthChange(e.target.value)}
            className="h-7 text-xs w-16"
          />
        </div>
      )}

      {frequency === "custom" && (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-16 shrink-0">Cron</span>
            <Input
              value={customDraft}
              readOnly={disabled}
              onChange={(e) => {
                setCustomDraft(e.target.value);
                setCustomError(null);
              }}
              onBlur={handleCustomBlur}
              className={cn("h-7 text-xs font-mono", customError && "border-destructive")}
              placeholder="* * * * *"
            />
          </div>
          {customError && <p className="text-[10px] text-destructive ml-[4.5rem]">{customError}</p>}
        </div>
      )}

      <div className="text-[10px] text-muted-foreground/60">
        {describeCron(
          frequency === "custom"
            ? value
            : buildCron(frequency, hours, minute, daysOfWeek, dayOfMonth),
        ) ?? value}
        {timezone ? ` (${timezone})` : ""}
      </div>
    </div>
  );
};
